import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Chip
} from '@mui/material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { userService } from '../services/userService';

// Lista de estados brasileiros
const ESTADOS_BRASIL = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
];

// Função para formatar CPF
const formatCPF = (value: string) => {
  const numbers = value.replace(/\D/g, '');
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

// Função para formatar telefone
const formatPhone = (value: string) => {
  const numbers = value.replace(/\D/g, '');
  return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

const RegisterUser: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Capturar associação do parâmetro da URL ou state
  const associationSlug = searchParams.get('association') || location.state?.associationSlug;
  
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
    phone: '',
    alternatePhone: '',
    cpf: '',
    birthDate: '',
    gender: '',
    city: '',
    state: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Aplicar formatação automática para CPF e telefone
    let formattedValue = value;
    if (name === 'cpf') {
      formattedValue = formatCPF(value);
    } else if (name === 'phone') {
      formattedValue = formatPhone(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  const validateCPF = (cpf: string) => {
    const numbers = cpf.replace(/\D/g, '');
    return numbers.length === 11;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validações
    if (!formData.name || !formData.password || !formData.cpf || !formData.city || !formData.state) {
      setError('Todos os campos obrigatórios devem ser preenchidos');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres');
      setLoading(false);
      return;
    }

    if (!validateCPF(formData.cpf)) {
      setError('CPF deve ter 11 dígitos');
      setLoading(false);
      return;
    }

    try {
      const registerData: any = {
        name: formData.name,
        password: formData.password,
        cpf: formData.cpf,
        city: formData.city,
        state: formData.state
      };

      if (formData.phone) registerData.phone = formData.phone;
      if (formData.alternatePhone) registerData.alternatePhone = formData.alternatePhone;
      if (formData.birthDate) registerData.birthDate = formData.birthDate;
      if (formData.gender) registerData.gender = formData.gender;

      await userService.register(registerData);

      setSuccess(true);
      setTimeout(() => {
        if (associationSlug) {
          navigate(`/login?association=${associationSlug}`);
        } else {
          navigate('/login');
        }
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom color="success.main">
            ✅ Conta Criada!
          </Typography>
          <Typography variant="body1" gutterBottom>
            Sua conta foi criada com sucesso!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecionando para o login...
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              Criar Conta de Usuário
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Crie sua conta para participar dos sorteios
            </Typography>
            {associationSlug && (
              <Box sx={{ mt: 2 }}>
                <Chip 
                  label={`Registrando para: ${associationSlug.charAt(0).toUpperCase() + associationSlug.slice(1)}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Nome Completo"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              margin="normal"
              variant="outlined"
            />

            <TextField
              fullWidth
              label="CPF"
              name="cpf"
              value={formData.cpf}
              onChange={handleInputChange}
              required
              margin="normal"
              variant="outlined"
              placeholder="000.000.000-00"
              inputProps={{ maxLength: 14 }}
              helperText="Formato: 000.000.000-00"
            />

            <TextField
              fullWidth
              label="Cidade"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              required
              margin="normal"
              variant="outlined"
            />

            <TextField
              fullWidth
              select
              label="Estado"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              required
              margin="normal"
              variant="outlined"
            >
              {ESTADOS_BRASIL.map((estado) => (
                <MenuItem key={estado.value} value={estado.value}>
                  {estado.value}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Telefone (opcional)"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
              placeholder="(11) 99999-9999"
              inputProps={{ maxLength: 15 }}
            />

            <TextField
              fullWidth
              label="Telefone Alternativo (opcional)"
              name="alternatePhone"
              value={formData.alternatePhone}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
              placeholder="(11) 99999-9999"
              inputProps={{ maxLength: 15 }}
            />

            <TextField
              fullWidth
              label="Data de Nascimento (opcional)"
              name="birthDate"
              type="date"
              value={formData.birthDate}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              select
              label="Gênero (opcional)"
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
            >
              <MenuItem value="">Prefiro não informar</MenuItem>
              <MenuItem value="Masculino">Masculino</MenuItem>
              <MenuItem value="Feminino">Feminino</MenuItem>
              <MenuItem value="Outro">Outro</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Senha"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              margin="normal"
              variant="outlined"
              helperText="Mínimo 8 caracteres"
            />

            <TextField
              fullWidth
              label="Confirmar Senha"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              margin="normal"
              variant="outlined"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? 'Criando conta...' : 'Criar Conta'}
            </Button>
          </form>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Já tem uma conta?
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                if (associationSlug) {
                  navigate(`/login?association=${associationSlug}`);
                } else {
                  navigate('/login');
                }
              }}
              sx={{ textDecoration: 'none' }}
            >
              Fazer Login
            </Link>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              É uma associação comercial?
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/users/register-association')}
              sx={{ textDecoration: 'none' }}
            >
              Cadastrar Associação
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}; 
export default RegisterUser;